/**
 * React Query hooks for the teacher surfaces.
 *
 * The query-key conventions:
 *   ['classrooms']                              — teacher's classroom list
 *   ['classroom', id]                           — one classroom
 *   ['classroom', id, 'students']               — roster
 *   ['classroom', id, 'assignments']            — pinned assignments
 *   ['classroom', id, 'live-view']              — per-student dashboard tiles
 *   ['classroom', id, 'review-queue']           — pending reviews
 *   ['classroom', id, 'gallery']                — class gallery
 *   ['classroom', id, 'submission', sid, 'feedback'] — rubric grading state
 *   ['assignments', 'curated', filters]         — lesson library
 *   ['assignments', 'standards']                — taxonomy
 *
 * Mutations invalidate the narrowest possible query key so a tile
 * refresh stays scoped — kids using a phone on school wifi notice
 * needless refetches.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addReaction,
  addStudent,
  approveSubmission,
  broadcastSubmission,
  clearBroadcast,
  createClassroom,
  deleteClassroom,
  getAssignment,
  getClassGallery,
  getClassroom,
  getClassroomSummary,
  getLiveView,
  getReviewQueue,
  getControlState,
  getStandardsTaxonomy,
  getSubmissionFeedback,
  listClassroomAssignments,
  listClassroomPublishedApps,
  listClassrooms,
  listCuratedAssignments,
  listStudents,
  patchClassroom,
  pauseClassroom,
  pinAssignmentToClassroom,
  putSubmissionFeedback,
  rejectSubmission,
  removeReaction,
  removeStudent,
  requestPublicPromotion,
  resetStudentPin,
  resumeClassroom,
  rotateClassCode,
  unpinAssignment,
  updateStudent,
} from './teacherApi';
import type { Classroom, FeedbackWrite, ListCuratedArgs } from './teacherApi';

export function useClassrooms() {
  return useQuery({
    queryKey: ['classrooms'],
    queryFn: listClassrooms,
  });
}

export function useClassroom(id: string | undefined) {
  return useQuery({
    queryKey: ['classroom', id],
    queryFn: () => getClassroom(id as string),
    enabled: Boolean(id),
  });
}

/** Kid-readable summary (id + name). Used by the student Home
 *  banner — `useClassroom` is teacher-only and 404s for students. */
export function useClassroomSummary(id: string | null | undefined) {
  return useQuery({
    queryKey: ['classroom', id, 'summary'],
    queryFn: () => getClassroomSummary(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateClassroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createClassroom(name),
    onSuccess: (created: Classroom) => {
      qc.invalidateQueries({ queryKey: ['classrooms'] });
      qc.setQueryData(['classroom', created.id], created);
    },
  });
}

export function usePatchClassroom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { name?: string; allow_publish?: boolean }) => patchClassroom(id, args),
    onSuccess: (updated) => {
      qc.setQueryData(['classroom', id], updated);
      qc.invalidateQueries({ queryKey: ['classrooms'] });
    },
  });
}

export function useRotateCode(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rotateClassCode(id),
    onSuccess: (updated) => {
      qc.setQueryData(['classroom', id], updated);
    },
  });
}

export function useDeleteClassroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteClassroom(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classrooms'] });
    },
  });
}

// --- Roster -----------------------------------------------------------

export function useStudents(classroomId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'students'],
    queryFn: () => listStudents(classroomId as string),
    enabled: Boolean(classroomId),
  });
}

/** Read every published app from kids on this classroom roster.
 *  Auto-refresh on a slow cadence so a teacher with the dashboard
 *  open sees new publishes within ~30s without a manual refresh. */
export function useClassroomPublishedApps(classroomId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'published-apps'],
    queryFn: () => listClassroomPublishedApps(classroomId as string),
    enabled: Boolean(classroomId),
    refetchInterval: 30_000,
  });
}

export function useAddStudent(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { display_name: string; pin: string }) => addStudent(classroomId, args),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'students'],
      });
    },
  });
}

export function useRemoveStudent(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (studentId: string) => removeStudent(classroomId, studentId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'students'],
      });
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'live-view'],
      });
    },
  });
}

export function useResetStudentPin(classroomId: string) {
  return useMutation({
    mutationFn: (args: { studentId: string; pin?: string }) =>
      resetStudentPin(classroomId, args.studentId, args.pin),
    // No invalidation: the response carries the new PIN in plaintext;
    // the roster list view doesn't need to refetch.
  });
}

export function useUpdateStudent(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { studentId: string; parent_contact_email?: string | null }) => {
      const { studentId, ...patch } = args;
      return updateStudent(classroomId, studentId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'students'],
      });
    },
  });
}

/** Phase 4.5b.14 — kid-side polling of classroom control state.
 *  Cheap (one indexed row read) so 5s polling is fine. The kid's
 *  studio mounts this when their `classroom_id` is set; an overlay
 *  appears as soon as `paused_at` flips non-null.
 *
 *  We DON'T poll on the teacher dashboard side via this hook — the
 *  control panel uses its own mutations that update the cache
 *  optimistically. Saves a redundant tab of polling traffic. */
export function useClassroomControl(classroomId: string | null | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'control'],
    queryFn: () => getControlState(classroomId as string),
    enabled: Boolean(classroomId),
    refetchInterval: 5_000,
    refetchOnWindowFocus: false,
  });
}

export function usePauseClassroom(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => pauseClassroom(classroomId),
    onSuccess: (state) => {
      qc.setQueryData(['classroom', classroomId, 'control'], state);
    },
  });
}

export function useResumeClassroom(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resumeClassroom(classroomId),
    onSuccess: (state) => {
      qc.setQueryData(['classroom', classroomId, 'control'], state);
    },
  });
}

/** Phase 4.5b.14 — broadcast half. Teacher spotlights one
 *  submission to the whole class. Same cache shape as pause/resume:
 *  the response IS the new control state, so we set it directly
 *  instead of refetching. Kids polling `useClassroomControl` get
 *  the new broadcast on their next 5s tick. */
export function useBroadcastSubmission(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => broadcastSubmission(classroomId, submissionId),
    onSuccess: (state) => {
      qc.setQueryData(['classroom', classroomId, 'control'], state);
    },
  });
}

export function useClearBroadcast(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clearBroadcast(classroomId),
    onSuccess: (state) => {
      qc.setQueryData(['classroom', classroomId, 'control'], state);
    },
  });
}

/** Phase 4.5c — teacher kicks off public-gallery promotion. The
 *  mutation returns the magic link in non-prod so the teacher can
 *  copy it for testing. In production the parent gets an email; the
 *  link is null. */
export function useRequestPublicPromotion(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => requestPublicPromotion(classroomId, submissionId),
    onSuccess: () => {
      // The grading page reads feedback; once the parent acts the
      // visibility flips. No need to invalidate immediately — the
      // change happens on the parent's tab.
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'gallery'],
      });
    },
  });
}

// --- Assignments + lesson library -------------------------------------

export function useCuratedAssignments(args: ListCuratedArgs = {}) {
  return useQuery({
    // Stable key — `args` is shallow-equal sensitive so identical
    // filter sets share a cache entry.
    queryKey: [
      'assignments',
      'curated',
      args.kind ?? null,
      args.standards ? [...args.standards].sort().join(',') : '',
      args.q ?? '',
    ],
    queryFn: () => listCuratedAssignments(args),
  });
}

export function useStandardsTaxonomy() {
  return useQuery({
    queryKey: ['assignments', 'standards'],
    queryFn: getStandardsTaxonomy,
    // The taxonomy is a static lookup — cache for the whole session.
    staleTime: Infinity,
  });
}

export function useAssignment(id: string | undefined) {
  return useQuery({
    queryKey: ['assignment', id],
    queryFn: () => getAssignment(id as string),
    enabled: Boolean(id),
  });
}

export function useClassroomAssignments(classroomId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'assignments'],
    queryFn: () => listClassroomAssignments(classroomId as string),
    enabled: Boolean(classroomId),
  });
}

export function usePinAssignment(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      pinAssignmentToClassroom(classroomId, { assignment_id: assignmentId }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'assignments'],
      });
    },
  });
}

export function useUnpinAssignment(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caId: string) => unpinAssignment(classroomId, caId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'assignments'],
      });
    },
  });
}

// --- Live class view --------------------------------------------------

export function useLiveView(classroomId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'live-view'],
    queryFn: () => getLiveView(classroomId as string),
    enabled: Boolean(classroomId),
    // The live view is a heartbeat. 10-second poll is enough for
    // teacher triage without hammering the API. The window-focus
    // refetch default is off, so the dashboard only refreshes when
    // the teacher is actively looking.
    refetchInterval: 10_000,
  });
}

// --- Review queue + gallery ------------------------------------------

export function useReviewQueue(classroomId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'review-queue'],
    queryFn: () => getReviewQueue(classroomId as string),
    enabled: Boolean(classroomId),
  });
}

export function useApproveSubmission(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => approveSubmission(classroomId, submissionId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'review-queue'],
      });
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'gallery'],
      });
    },
  });
}

export function useRejectSubmission(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { submissionId: string; reason: string; detail?: string }) =>
      rejectSubmission(classroomId, args.submissionId, {
        reason: args.reason,
        ...(args.detail !== undefined ? { detail: args.detail } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'review-queue'],
      });
    },
  });
}

export function useClassGallery(classroomId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'gallery'],
    queryFn: () => getClassGallery(classroomId as string),
    enabled: Boolean(classroomId),
  });
}

export function useToggleReaction(classroomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      submissionId: string;
      code: string;
      // The current state — when true, we remove; when false, we add.
      hasReacted: boolean;
    }) => {
      if (args.hasReacted) {
        return removeReaction(classroomId, args.submissionId, args.code);
      }
      return addReaction(classroomId, args.submissionId, args.code);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['classroom', classroomId, 'gallery'],
      });
    },
  });
}

// --- Feedback ---------------------------------------------------------

export function useFeedback(classroomId: string | undefined, submissionId: string | undefined) {
  return useQuery({
    queryKey: ['classroom', classroomId, 'submission', submissionId, 'feedback'],
    queryFn: () => getSubmissionFeedback(classroomId as string, submissionId as string),
    enabled: Boolean(classroomId && submissionId),
  });
}

export function usePutFeedback(classroomId: string, submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FeedbackWrite) => putSubmissionFeedback(classroomId, submissionId, body),
    onSuccess: (updated) => {
      qc.setQueryData(['classroom', classroomId, 'submission', submissionId, 'feedback'], updated);
    },
  });
}
